<template>
  <v-card outlined rounded class="rounded-lg">
    <v-toolbar flat>
      <v-toolbar-title>Tracciati</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn text @click.prevent="download('R1')"><v-icon>mdi-download</v-icon> Scarica</v-btn>
    </v-toolbar>
    <v-card-text>
      <v-textarea name="" id="" cols="30" rows="10" v-model="R1toText" class="mono" outlined label="R1"></v-textarea>
      <v-textarea name="" id="" cols="30" rows="10" v-model="R2toText" class="mono" outlined label="R2"></v-textarea>
    </v-card-text>
  </v-card>
</template>

<script>
// Managers
import { SurveyManager } from '@/models/v1/managers'

// Models
import Journey from '@/models/v1/abstract-journey.model'
import StandardJourney from '@/models/v1/standard-journey.model'

// Third-party libs
import { saveAs } from 'file-saver'
import { mapState } from 'vuex'

// Component
export default {
  data() {
    return {
      rows: [],
      records: {
        R1: [],
        R2: [],
      },
    }
  },
  computed: {
    ...mapState('survey', ['survey']),
    R1toText() {
      return this.records.R1.map((r) => r.join('')).join('\n')
    },
    R2toText() {
      return this.records.R2.map((r) => r.join('')).join('\n')
    },
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    download(record) {
      let blobR1 = new Blob([this.R1toText], {
        type: 'text/plain;charset=utf-8',
      })
      saveAs(blobR1, 'R1.txt')
      let blobR2 = new Blob([this.R2toText], {
        type: 'text/plain;charset=utf-8',
      })
      saveAs(blobR2, 'R2.txt')
    },
    makeR1rows(survey, journeys, { loaded, empty }) {
      const filler = '0'

      let startDate = new Date(survey.start)
      let formattedStartDate = [
        `${startDate.getDate()}`.padStart(2, filler),
        `${startDate.getMonth() + 1}`.padStart(2, filler),
        `${startDate.getFullYear()}`.substring(2, 4).padStart(2, filler),
      ].join('')

      let endDate = new Date(survey.end)
      let formattedEndDate = [
        `${endDate.getDate()}`.padStart(2, filler),
        `${endDate.getMonth() + 1}`.padStart(2, filler),
        `${endDate.getFullYear()}`.substring(2, 4).padStart(2, filler),
      ].join('')

      const fleetTypes = ['vehicles', 'tractors', 'trailers', 'semitrailers']
      const fleetCounters = ['owned', 'leasing', 'rent']

      const surveyRecordColumns = [
        `${survey.week}`.padStart(2, filler), // settimana di riferimento
        `${survey.quarter}`.padStart(1, filler), // trimestre
        formattedStartDate.padStart(6, filler), // dal giorno/mese/anno (ggmmaa)
        formattedEndDate.padStart(6, filler), // al giorno/mese/anno (ggmmaa)
        survey.province_imm.padStart(2, filler), // provincia di immatricolazione
        survey.targa.padStart(7, filler), // targa
        `${loaded}`.padStart(4, filler), // km percorsi con carico nella settimana
        `${empty}`.padStart(4, filler), // km percorsi a vuoto nella settimana
        `${survey.vehicle.activity.distance.fuel}`.padStart(4, filler), // carburante consumato
        `${survey.owner.ateco}`.padStart(4, filler), // attività prevalente,
      ]

      fleetTypes.forEach((type) => {
        fleetCounters.forEach((counter) => {
          surveyRecordColumns.push(`${survey.fleet[type][counter]}`.padStart(4, '0'))
        })
      })

      let rows = []

      if (journeys.length > 0) {
        rows = journeys
          .filter(
            (j) => j.type === 1
            // j =>
            //   j._type === Journey._STANDARD_MONO ||
            //   j._type === Journey._STANDARD_MULTI ||
            //   j._type === Journey._STANDARD_EMPTY
          )
          .map((journey) => {
            // Viaggio
            return [
              `${journey._type}`.padStart(1, filler),
              ...surveyRecordColumns,
              ...StandardJourney.getRecordColumns(journey),
            ]
          })
      } else {
        return [[`0`.padStart(1, filler), ...surveyRecordColumns, '0'.padStart(134, filler)]]
      }

      return rows
    },
    makeR2rows(survey, journeys) {
      const filler = '0'

      const surveyRecordColumns = [
        `${survey.week}`.padStart(2, filler), // settimana di riferimento
        `${survey.quarter}`.padStart(1, filler), // trimestre
        survey.province_imm.padStart(2, filler), // provincia di immatricolazione
        survey.targa.padStart(7, filler), // targa
      ]

      return journeys
        .filter(
          (j) =>
            j._type === Journey._ROUND || j._type === Journey._SHUTTLE || j._type === Journey._ROUND_OR_SHUTTLE_EMPTY
        )
        .map((journey) => {
          // Viaggio
          let columns = []
          columns.push(
            `${journey.coupled_vehicle.type === 1 ? journey.coupled_vehicle.load_capacity : ''}`.padStart(3, filler)
          ) // rimorchio/portata utile
          columns.push(`${journey.coupled_vehicle.type === 1 ? journey.coupled_vehicle.axles : ''}`.padStart(2, filler)) // rimorchio/n. assi
          columns.push(
            `${journey.coupled_vehicle.type === 2 ? journey.coupled_vehicle.load_capacity : ''}`.padStart(3, filler)
          ) // semirimorchio/portata utile
          columns.push(`${journey.coupled_vehicle.type === 2 ? journey.coupled_vehicle.axles : ''}`.padStart(2, filler)) // semirimorchio/n. assi
          columns.push(`${journey.date.replace('/', '')}`.padStart(4, filler)) // data del viaggio GGMM

          columns.push(`${journey.origin.place.cod_com}`.padStart(3, filler)) // comune di origine
          columns.push(`${journey.origin.place.cod_prv}`.padStart(3, filler)) // provincia di origine
          columns.push(`${journey.destination.place.cod_com}`.padStart(3, filler)) // comune di destinazione
          columns.push(`${journey.destination.place.cod_prv}`.padStart(3, filler)) // provincia di destinazione

          columns.push(`${journey.goods[0].type}`.padStart(2, filler)) // tipo merce raccolta 1
          columns.push(`${journey.goods[0].dangerous_type}`.padStart(3, filler)) // merce pericolosa raccolta 1
          columns.push(`${journey.goods[0].weight}`.padStart(3, filler)) // peso merce raccolta 1
          columns.push(`${journey.goods[0].aspect}`.padStart(2, filler)) // aspetto del carico raccolto 1

          columns.push(`${journey.goods[1].type}`.padStart(2, filler)) // tipo merce distribuita 2
          columns.push(`${journey.goods[1].dangerous_type}`.padStart(3, filler)) // merce pericolosa distribuita 2
          columns.push(`${journey.goods[1].weight}`.padStart(3, filler)) // peso merce distribuita 2
          columns.push(`${journey.goods[1].aspect}`.padStart(1, filler)) // aspetto del carico distribuito 2

          columns.push(`${journey.distance.loaded}`.padStart(4, filler)) // distanza percorsa con carico
          columns.push(`${journey.distance.empty}`.padStart(4, filler)) // distanza percorsa con a vuoto

          columns.push(`${journey.num_stops ? journey.num_stops : ''}`.padStart(3, filler)) // n. fermate
          columns.push(`${journey.num_stops_prv ? journey.num_stops_prv : ''}`.padStart(3, filler)) // n. fermate nella provincia di origine
          columns.push(`${journey.num_journeys}`.padStart(3, filler)) // n. di viaggi
          columns.push(`${journey._type}`.padStart(1, filler)) // tipo di circuito/navetta
          columns.push(`?`.padStart(1, filler)) // è compilato lo schema viaggi - tipo viaggio

          return [`${journey.type}`.padStart(1, filler), ...surveyRecordColumns, ...columns]
        })
    },
  },
  mounted() {
    let manager = new SurveyManager(this.survey)
    let journeys = manager.getRestructuredJourneys()

    let { loaded, empty } = manager.calculateTotalDistances(journeys)

    this.records.R1 = this.makeR1rows(this.survey, [...journeys], {
      loaded,
      empty,
    })
    this.records.R2 = this.makeR2rows(this.survey, [...journeys])
  },
}
</script>

<style lang="scss" scoped>
.mono {
  font-family: monospace;
  font-size: small;
  line-height: 4px;
}
</style>
